import type { OAuth2Config, OAuth2FormValues } from '#layers/access/utils/auth-providers/protocols/oauth2';
import type { FormSchemaResolved } from '#layers/block-engine/public';
import { AuthProtocol, defineAuthProvider } from '#layers/access/types/auth-providers/define';
import { oauth2 } from '#layers/access/utils/auth-providers/protocols/oauth2';
import {
	applyOAuth2PresetOptions,
	normalizeDomain,
	OIDC_CLAIM_MAPPINGS,
	OIDC_SCOPES,
	parseOAuth2PresetAuthorizationHostname,
	promoteOAuth2PresetFields,
} from './oauth2-preset';

interface OktaFormValues extends Omit<OAuth2FormValues, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	authorizationEndpoint: string;
}

const OKTA_DEFAULTS = {
	'scopes': OIDC_SCOPES,
	'claimMappings.sub': OIDC_CLAIM_MAPPINGS.sub,
	'claimMappings.email': OIDC_CLAIM_MAPPINGS.email,
} as const;

const OKTA_WEB_APP_REDIRECT_URL = 'https://developer.okta.com/docs/guides/sign-into-web-app-redirect/';

function oktaEndpoints(domainInput: string): Pick<OAuth2Config, 'authorizationEndpoint' | 'tokenEndpoint' | 'userinfoEndpoint'> {
	const domain = normalizeDomain(domainInput, '.okta.com');

	return {
		authorizationEndpoint: `https://${domain}/oauth2/v1/authorize`,
		tokenEndpoint: `https://${domain}/oauth2/v1/token`,
		userinfoEndpoint: `https://${domain}/oauth2/v1/userinfo`,
	};
}

function parseOktaDomain(config: unknown): Partial<OktaFormValues> {
	const parsed = oauth2.parseInitialValues?.(config as OAuth2Config) ?? {};
	return parseOAuth2PresetAuthorizationHostname(config, parsed);
}

function applyOktaDefaults(items: FormSchemaResolved<any>): FormSchemaResolved<any> {
	return promoteOAuth2PresetFields(applyOAuth2PresetOptions(items, {
		defaults: OKTA_DEFAULTS,
		omitFieldNames: ['tokenEndpoint', 'userinfoEndpoint'],
		omitSectionLabels: ['$t:sso_providers.oauth2.section.endpoints'],
		fieldOverrides: {
			authorizationEndpoint: {
				label: '$t:sso_providers.okta.domain_label',
				// Drop the inherited authorize-URL hint; the placeholder already shows the expected domain.
				description: undefined,
				required: true,
				props: { placeholder: '$t:sso_providers.okta.domain_placeholder' },
				default: '',
			},
		},
	}), ['authorizationEndpoint', 'clientId', 'clientSecret']);
}

export const okta = defineAuthProvider<typeof oauth2, OAuth2Config, OktaFormValues>({
	id: 'okta',
	name: '$t:sso_providers.okta.name',
	description: '$t:sso_providers.okta.description',
	protocol: AuthProtocol.OAuth2,
	icon: {
		default: 'logos:okta-icon',
		dark: 'simple-icons:okta',
	},
	docsUrl: OKTA_WEB_APP_REDIRECT_URL,
	walkthroughStepKeys: [
		{
			key: 'sso_providers.okta.walkthrough.create_app',
			href: OKTA_WEB_APP_REDIRECT_URL,
		},
		{ key: 'sso_providers.okta.walkthrough.redirect', showCallbackUrl: true },
		'sso_providers.okta.walkthrough.credentials',
	],
	extends: oauth2,
	options: ({ form, context }) =>
		form(applyOktaDefaults(context.base?.resolvedOptions ?? []) as never),
	transformOptions: (form): OAuth2Config =>
		oauth2.transformOptions!({
			...form,
			...oktaEndpoints(form.authorizationEndpoint),
		}),
	parseInitialValues: parseOktaDomain,
});
